import React, { useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Loader2, LogIn, AlertCircle, MessageSquare } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useChat } from '../context/ChatContext';
import { ChatMessage, ChatInput, ExportSingleCalculation, ExportFullChat } from '../components/features';
import { Button } from '../components/ui';

/**
 * Chat View Component
 * 
 * Full chat interface for continuing conversations
 * Anonymous users can view but not send follow-up messages
 */
const ChatView = () => {
  const { user } = useAuth();
  const { 
    activeChat, 
    setActiveChat, 
    sendMessage, 
    isLoading,
    getMaterialIcon,
    getRemainingFollowUps,
    isPro,
    getLimitsConfig
  } = useChat();
  const messagesEndRef = useRef(null);
  
  // Check if user can send follow-ups
  const canSendFollowUp = !!user;
  
  // Get remaining follow-ups for this chat
  const remainingFollowUps = activeChat ? getRemainingFollowUps(activeChat) : 0;
  const limits = getLimitsConfig();
  const maxFollowUps = isPro ? limits.proFollowUpsPerChat : limits.freeFollowUpsPerChat;

  // Scroll to bottom on new messages
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [activeChat?.messages]);

  const handleSendMessage = async (messageInput) => {
    if (!messageInput.trim() || isLoading) return;

    try {
      await sendMessage(messageInput, true, activeChat.id, activeChat.material_type);
    } catch (error) {
      console.error('Error sending message:', error);
    }
  };

  const handleBack = () => {
    setActiveChat(null);
  };

  if (!activeChat) return null;

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      {/* Header - Sticky for quick access */}
      <div className="bg-white border-b sticky top-0 z-20 shadow-sm">
        <div className="max-w-4xl mx-auto px-4 py-3 flex items-center justify-between">
          <button
            onClick={handleBack}
            className="flex items-center gap-2 text-brand-700 hover:text-brand-800 font-semibold transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500/30 rounded-lg px-2 py-1"
            aria-label="Back to home page"
          >
            <ArrowLeft className="w-5 h-5" aria-hidden="true" />
            <span className="hidden sm:inline">Back to Home</span>
            <span className="sm:hidden">Back</span>
          </button>
          
          <div className="flex items-center gap-3">
            {/* PDF Export - Only for signed-in users */}
            {canSendFollowUp && activeChat.messages?.length >= 2 && (
              <div className="hidden sm:flex items-center gap-2">
                <ExportSingleCalculation 
                  messages={activeChat.messages}
                  materialType={activeChat.material_type}
                  isLoading={isLoading}
                />
                {activeChat.messages.length > 2 && (
                  <ExportFullChat
                    messages={activeChat.messages}
                    materialType={activeChat.material_type}
                    isLoading={isLoading}
                  />
                )}
              </div>
            )}
            
            {/* Material Type Badge */}
            <div className="flex items-center gap-2 text-gray-600 bg-gray-100 px-3 py-1.5 rounded-full">
              <span className="text-lg">{getMaterialIcon(activeChat.material_type)}</span>
              <span className="capitalize font-medium text-sm">{activeChat.material_type}</span>
            </div>
          </div>
        </div>
        
        {/* Mobile PDF Export */}
        {canSendFollowUp && activeChat.messages?.length >= 2 && (
          <div className="sm:hidden border-t bg-gray-50 px-4 py-2 flex items-center justify-center gap-2">
            <ExportSingleCalculation 
              messages={activeChat.messages}
              materialType={activeChat.material_type}
              isLoading={isLoading}
            />
            {activeChat.messages.length > 2 && (
              <ExportFullChat
                messages={activeChat.messages}
                materialType={activeChat.material_type}
                isLoading={isLoading}
              />
            )}
          </div>
        )}
      </div>

      {/* Messages */}
      <div className="flex-1 overflow-y-auto">
        <div className="max-w-4xl mx-auto px-4 py-6 space-y-4">
          {activeChat.messages?.map((msg, idx) => (
            <ChatMessage
              key={idx}
              role={msg.role}
              content={msg.content}
            />
          ))}

          {isLoading && (
            <div className="flex justify-start">
              <div className="flex gap-3 max-w-[85%]">
                <div className="w-8 h-8 rounded-full bg-gray-200 flex-shrink-0 flex items-center justify-center">
                  <Loader2 className="w-4 h-4 text-gray-600 animate-spin" />
                </div>
                <div className="bg-white border border-gray-200 rounded-2xl rounded-tl-sm px-4 py-3 shadow-sm">
                  <div className="flex items-center gap-2 text-gray-500">
                    <span>Calculating</span>
                    <span className="flex gap-1">
                      <span className="w-1.5 h-1.5 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                      <span className="w-1.5 h-1.5 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                      <span className="w-1.5 h-1.5 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
                    </span>
                  </div>
                </div>
              </div>
            </div>
          )}

          <div ref={messagesEndRef} />
        </div>
      </div>

      {/* Input - Only for signed-in users */}
      <div className="bg-white border-t sticky bottom-0">
        <div className="max-w-4xl mx-auto px-4 py-4">
          {canSendFollowUp ? (
            <>
              {/* Follow-up limit indicator */}
              {remainingFollowUps <= 3 && remainingFollowUps > 0 && !isPro && (
                <div className="mb-3 flex items-center justify-center gap-2 text-sm text-amber-600 bg-amber-50 rounded-lg py-2 px-3">
                  <MessageSquare className="w-4 h-4" />
                  <span>{remainingFollowUps} follow-up{remainingFollowUps !== 1 ? 's' : ''} remaining in this chat</span>
                </div>
              )}
              
              {remainingFollowUps === 0 ? (
                <div className="bg-gradient-to-r from-red-50 to-orange-50 rounded-xl p-4 border border-red-100">
                  <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
                    <div className="flex items-center gap-3 text-center sm:text-left">
                      <AlertCircle className="w-6 h-6 text-red-500 flex-shrink-0" />
                      <div>
                        <p className="font-medium text-gray-900">Follow-up limit reached</p>
                        <p className="text-sm text-gray-600">
                          You've used all {maxFollowUps} follow-ups for this calculation. 
                          {!isPro && ' Upgrade for more, or'} Start a new calculation to continue.
                        </p>
                      </div>
                    </div>
                    <Link to="/">
                      <Button variant="primary">
                        New Calculation
                      </Button>
                    </Link>
                  </div>
                </div>
              ) : (
                <ChatInput
                  onSend={handleSendMessage}
                  isLoading={isLoading}
                  placeholder="Ask a follow-up question..."
                  showExamples={false}
                  size="md"
                />
              )}
            </>
          ) : (
            <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl p-4 border border-blue-100">
              <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
                <div className="text-center sm:text-left">
                  <p className="font-medium text-gray-900">Want to continue this conversation?</p>
                  <p className="text-sm text-gray-600">Sign in to ask follow-up questions and save your calculations.</p>
                </div>
                <Link to="/signin">
                  <Button icon={LogIn}>
                    Sign In to Continue
                  </Button>
                </Link>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ChatView;
